import type { TFunction } from 'i18next';
import type { CaughtApiError } from '../types';

const CODE_TO_KEY: Record<string, string> = {
  INVALID_REQUEST_BODY: 'errors.api.invalidBody',
  VALIDATION_ERROR: 'errors.api.validation',
  JOB_NOT_COMPLETED: 'errors.api.jobNotCompleted',
  QUOTE_ALREADY_SENT: 'errors.api.quoteAlreadySent',
  QUOTE_CANNOT_BE_ACCEPTED: 'errors.api.quoteNotAcceptable',
  JOB_NOT_ACCEPTING_QUOTES: 'errors.api.jobNotAcceptingQuotes',
  CANNOT_QUOTE_OWN_JOB: 'errors.api.cannotQuoteOwnJob',
  JOB_ALREADY_ACCEPTED: 'errors.api.quoteNotAcceptable',
  PROVIDER_CATEGORY_MISMATCH: 'errors.api.quoteOutOfScope',
  PROVIDER_OUT_OF_RANGE: 'errors.api.quoteOutOfScope',
  INVALID_PHONE: 'validation.phoneInvalid',
  PHONE_ALREADY_IN_USE: 'auth.phoneAlreadyUsed',
};

/** Narrowed HTTP status of an error thrown by the axios service layer. */
export function getApiErrorStatus(error: unknown): number | undefined {
  return (error as CaughtApiError | null)?.status;
}

/** True when the request failed for lack of a usable answer (network, timeout, rate limit, 5xx): says nothing about the session. */
export function isTransientApiError(error: unknown): boolean {
  const status = getApiErrorStatus(error);
  return status === undefined || status === 408 || status === 429 || status >= 500;
}

/** True when the backend refuses access to (or hides) a resource: 403 or 404. */
export function isForbiddenOrNotFoundError(error: unknown): boolean {
  const status = getApiErrorStatus(error);
  return status === 403 || status === 404;
}

/**
 * Returns a translated, user-facing message for an API error.
 * Never exposes raw backend codes or English server messages.
 */
export function getApiErrorMessage(error: unknown, t: TFunction, fallbackKey = 'common.error'): string {
  const e = error as CaughtApiError | null;
  const code = typeof e?.code === 'string' ? e.code : undefined;

  if (code && CODE_TO_KEY[code]) return t(CODE_TO_KEY[code]);

  switch (e?.status) {
    case 403:
      return t('errors.api.forbidden');
    case 404:
      return t('errors.api.notFound');
    case 409:
      return t('errors.api.conflict');
    default:
      return t(fallbackKey);
  }
}
